import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { PhoneNumberFormat as PNF, PhoneNumberUtil } from 'google-libphonenumber';
import { Organization } from '../../../model/organization';
import { OrganizationsService } from '../../../services/organizations.service';
import { OrganizationPersistRequest } from '../../../model/requests/organization-persist-request';
import { Tenant } from '../../../model/tenant';
import { TenantsService } from '../../../services/tenants.service';
import { ComponentCanDeactivate } from '../../../common/can-deactivate/component-can-deactivate';
import { AuthGuard } from '../../../common/auth-guard.service';
import { AppConfig } from '../../../shared/global-variables';
import { noSpecialCharacters, noWhitespaces } from '../../../shared/custom-validators';
import { CommonMessagesService } from '../../../services/common-messages.service';
import * as moment from "moment";

const phoneUtil = PhoneNumberUtil.getInstance();

@Component({
  selector: 'app-organizations-form',
  templateUrl: './organizations-form.component.html',
  styleUrls: ['./organizations-form.component.scss']
})
export class OrganizationsFormComponent implements OnInit, ComponentCanDeactivate {

  public organization: Organization;

  public tenants: Tenant[];

  public organizationForm: FormGroup;
  public persistingChanges: any;
  public textFields: string[];
  public formSuccess: boolean;

  public separateDialCode = false;
  public SearchCountryField = SearchCountryField;
  public CountryISO = CountryISO;
  public PhoneNumberFormat = PhoneNumberFormat;
  public PhoneNumberUtil = PhoneNumberUtil;
  public preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedKingdom];

  public fromDate: Date;
  public entryDate: Date;

  public mode: 'create' | 'edit';

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private tenantsService: TenantsService,
              private authGuard: AuthGuard,
              private organizationsService: OrganizationsService,
              private commonMessagesService: CommonMessagesService) {
    this.fromDate = moment().startOf('day').toDate();
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      const firstPath = urlSegment[0].path;
      if (firstPath === 'new') {
        this.mode = 'create';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadCreationForm();
        });
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }

  loadCreationForm(): void {
    this.organization = new Organization();
    if (!this.authGuard.superAdminRequired()) {
      this.organization.tenant = this.tenants[0];
    }
    this.createForm(this.organization);
  }

  loadEditionForm(uuid): void {
    this.organizationsService.getOrganization(uuid).toPromise().then(response => {
      this.organization = response.organization;
      this.tenants = [this.organization.tenant];
      this.parsePhoneNumber(this.organization);
      this.createForm(this.organization);
    });
  }

  private createForm(organization: Organization): void {
    this.organizationForm = this.formBuilder.group({
      name: [organization.name, [
        Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH),
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters,
        noWhitespaces
      ]],
      description: [organization.description, [Validators.maxLength(255)]],
      email: [organization.email, [Validators.required, Validators.maxLength(255), Validators.email]],
      phone: [organization.phone, [Validators.maxLength(255)]],
      address: [organization.address, [Validators.maxLength(255)]],
      address2: [organization.address2, [Validators.maxLength(255)]],
      city: [organization.city, [Validators.maxLength(255)]],
      zip: [organization.zip, [Validators.maxLength(255)]],
      region: [organization.region, [Validators.maxLength(255)]],
      country: [organization.country, [Validators.maxLength(255)]],
      tenant: [organization.tenant, [Validators.maxLength(255), Validators.required]],
      entryDate: [organization.entryDate ? new Date(organization.entryDate) : this.fromDate, [Validators.required]],
      expirationDate: [organization.expirationDate ? new Date(organization.expirationDate) : null]
    });
    this.organizationForm.markAsPending();

    if (this.mode === 'edit') {
      this.disableFixedFields();
    }

    if (!this.authGuard.superAdminRequired()) {
      this.organizationForm.get('tenant').disable();
    }

    this.textFields = [];
    const alreadyDefinedFields = ['name', 'description', 'tenant', 'entryDate', 'expirationDate'];
    const formFields = Object.keys(this.organizationForm.getRawValue());
    for (const field of formFields) {
      if (!alreadyDefinedFields.includes(field)) {
        this.textFields.push(field);
      }
    }
  }

  private disableFixedFields(): void {
    this.organizationForm.get('tenant').disable();
  }

  changeEntryDate(): void {
    this.entryDate = this.organizationForm.get('entryDate').value;
    this.organizationForm.get('expirationDate').setValue(null);
  }

  persistOrganizationData(): void {
    if (!this.organizationForm.valid) { return; }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.organizationsService.createOrganization(persistData).toPromise();
    } else {
      promise = this.organizationsService.editOrganization(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/management/organizations/`]);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): OrganizationPersistRequest {
    const formValue = this.organizationForm.getRawValue();
    return {
      name: formValue.name,
      description: formValue.description,
      address: formValue.address,
      address2: formValue.address2,
      city: formValue.city,
      country: formValue.country,
      email: formValue.email,
      phone: formValue.phone ? formValue.phone.e164Number : null,
      region: formValue.region,
      zip: formValue.zip,
      tenantUuid: formValue.tenant.uuid,
      entryDate: formValue.entryDate.getTime(),
      expirationDate: formValue.expirationDate ? formValue.expirationDate.getTime() : null,
      uuid: this.mode === 'edit' ? this.organization.uuid : null
    };
  }

  private parsePhoneNumber(organization: Organization): void {
    try {
      const rawPhone = organization.phone;
      const firstNumber = phoneUtil.parse(rawPhone);
      const countryCode = firstNumber.getCountryCode();
      const regionCode = phoneUtil.getRegionCodeForNumber(firstNumber);

      const e164 = phoneUtil.format(firstNumber, PNF.E164);
      const nationalNumber = phoneUtil.format(firstNumber, PNF.National);
      const internationalNumber = phoneUtil.format(firstNumber, PNF.National);

      this.organization.phone = {
        countryCode: regionCode,
        dialCode: `+${countryCode}`,
        e164Number: e164,
        internationalNumber: `+${countryCode} ${internationalNumber}`,
        nationalNumber,
        number: nationalNumber
      };
    } catch (e) {
      this.organization.phone = {};
    }
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.organizationForm.untouched;
  }

}
